import { useState } from 'react';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import Locale from 'Locale';
import { type DuplicateOptions } from './ContentItemEditor.types';

type Props = {
    open: boolean;
    itemTitle: string;
    itemLocale: string;

    // The titles already in this locale pack, keyed by locale code. A duplicate into a locale that
    // is taken replaces the item there, so the dialog says so before the admin commits.
    titlesByLocale: Record<string, string>;
    saving: boolean;
    onCancel: () => void;
    onConfirm: (options: DuplicateOptions) => void;
};

/*
    The React version of the Angular duplicate-content-item directive, for the course editor. The
    Angular directive stays where it is for the playlist editor, the lesson editor and the playlist
    save panel.

    The Angular heading above the locale picker read 'Lesson Locale' on every page, including this
    one. It reads 'Locale' here.
*/
export default function DuplicateContentItemDialog({
    open,
    itemTitle,
    itemLocale,
    titlesByLocale,
    saving,
    onCancel,
    onConfirm,
}: Props) {
    const localeOptions = Locale.editorLocalesWithoutEnglish.filter(locale => locale.code !== itemLocale);
    const [title, setTitle] = useState(`Duplicated from '${itemTitle}'`);
    const [isTranslation, setIsTranslation] = useState(false);
    const [locale, setLocale] = useState(localeOptions[0]?.code || '');

    /*
        The item's own locale is never a translation target, so it is not among the options. Angular
        re-pointed the picker whenever the two collided; clamping here keeps both the Select and the
        request on a locale that is still offered, whatever the state holds.
    */
    const selectedLocale = localeOptions.some(option => option.code === locale) ? locale : localeOptions[0]?.code || '';

    const replacedTitle = isTranslation ? titlesByLocale[selectedLocale] : undefined;

    return (
        <Dialog open={open} onClose={onCancel} fullWidth maxWidth="xs">
            <DialogTitle>Duplicate</DialogTitle>

            <DialogContent>
                <Stack spacing={2} sx={{ paddingTop: 1 }}>
                    <TextField
                        name="title"
                        label="Title"
                        value={title}
                        onChange={event => setTitle(event.target.value)}
                        fullWidth
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                name="is-translation"
                                checked={isTranslation}
                                onChange={event => setIsTranslation(event.currentTarget.checked)}
                            />
                        }
                        label="Is a translation?"
                    />

                    {isTranslation && (
                        <>
                            <TextField
                                select
                                name="locale"
                                label="Locale"
                                value={selectedLocale}
                                onChange={event => setLocale(event.target.value)}
                                fullWidth
                            >
                                {localeOptions.map(option => (
                                    <MenuItem key={option.code} value={option.code}>
                                        {option.title}
                                    </MenuItem>
                                ))}
                            </TextField>

                            {!!replacedTitle && (
                                <Typography variant="body2" color="warning.main">
                                    {`This translation will replace ${replacedTitle}`}
                                </Typography>
                            )}
                        </>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onCancel} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => onConfirm({ title, locale: selectedLocale, isTranslation })}
                    disabled={saving || (isTranslation && !selectedLocale)}
                >
                    Duplicate
                </Button>
            </DialogActions>
        </Dialog>
    );
}
